// utils/exportClassGroupsToExcel.js
//
// Builds an Arabic, RTL-formatted .xlsx file from a list of ClassGroup
// objects (each carrying a `students` array of enrollments with the full
// Student row attached) and lets the user pick a save location through the
// File System Access API (with a download fallback for browsers that lack it).
//
// Layout:
//   - One worksheet per className (الصف). All rooms (الشعب) of that الصف
//     end up on the same sheet, separated by a colored "الشعبة رقم: N" band
//     before each room's rows.
//   - Columns are exactly the Student fields the user picked in the modal —
//     no auto-added join columns, since the className is the sheet name and
//     the room is a band.
//   - Date-typed Student fields are written as real Date values with a
//     dd/mm/yyyy number format so Excel recognises them as dates instead of
//     showing the raw ISO string.

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Order matters — used as the canonical Student column order in the modal
// AND in the generated sheets when the user picks "all".
export const STUDENT_FIELD_LABELS = {
  serialNumber: "الرقم التسلسلي",
  FirstName: "اسم التلميذ",
  LastName: "اللقب",
  FatherName: "اسم الأب",
  GrandFatherName: "اسم الجد",
  MotherName: "اسم الأم",
  sex: "الجنس",
  birthDate: "تاريخ الميلاد",
  birthPlace: "مكان الولادة",
  Registration: "محل ورقم القيد",
  Nationality: "الجنسية",
  Religion: "الديانة",
  Language: "اللغة الأجنبية",
  mobile: "رقم الموبايل",
  phone: "رقم الأرضي",
  HistoryOfRegistration: "تاريخ الانتساب للمدرسة",
  previousSchool: "المدرسة التي انتقل منها",
  NumberOfDocument: "رقم وثيقته عند الانتساب",
  HistoryOfDocument: "تاريخ وثيقته عند الانتساب",
  ClassOfRegistration: "الصف الذي انتسب اليه",
  FailClass: "صفوف الرسوب السابقة",
  notes: "ملاحظات",
};

export const STUDENT_FIELD_KEYS = Object.keys(STUDENT_FIELD_LABELS);

// Fields that hold dates. birthDate / HistoryOfRegistration are DATEONLY in
// the Sequelize model. HistoryOfDocument is a STRING but historically holds
// formatted dates, so we treat any value matching YYYY-MM-DD as a date when
// it appears in those columns.
const DATE_FIELDS = new Set([
  "birthDate",
  "HistoryOfRegistration",
  "HistoryOfDocument",
]);

const ARABIC_CLASS_ORDER = [
  "الأول",
  "الثاني",
  "الثالث",
  "الرابع",
  "الخامس",
  "السادس",
  "السابع",
  "الثامن",
  "التاسع",
  "العاشر",
  "الحادي عشر",
];

// Excel sheet names cannot contain : \ / ? * [ ] and must be ≤ 31 chars
const sanitizeSheetName = (raw) => {
  const cleaned = String(raw || "بدون اسم").replace(/[\\/?*[\]:]/g, "");
  return cleaned.slice(0, 31) || "بدون اسم";
};

const looksLikeIsoDate = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v);

const toExcelValue = (key, value) => {
  if (value == null || value === "") return "";
  if (DATE_FIELDS.has(key) && looksLikeIsoDate(value)) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  return value;
};

const sortClassNames = (names) =>
  names.slice().sort((a, b) => {
    const ai = ARABIC_CLASS_ORDER.indexOf(a);
    const bi = ARABIC_CLASS_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return String(a).localeCompare(String(b), "ar");
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

/**
 * Generate the workbook and prompt the user to pick a save location.
 *
 * @param {Object}   args
 * @param {Array}    args.classGroups       — full ClassGroup rows including
 *                                            { className, roomNumber, academicYear,
 *                                              students:[{ student:{...full Student row} }] }
 * @param {string[]} args.selectedFields    — Student field keys the user chose
 * @param {boolean}  [args.groupByRoom=true] — when true (default), each sheet's
 *                                            rows are banded by "الشعبة رقم: N";
 *                                            when false, every student in the
 *                                            same الصف is listed continuously
 *                                            with no room separators.
 * @param {string}   [args.suggestedFileName="الطلاب"]
 *
 * @returns {Promise<{ saved: boolean, cancelled?: boolean, method?: 'picker'|'download', fileName?: string }>}
 */
export async function generateAndSaveStudentsExcel({
  classGroups,
  selectedFields,
  groupByRoom = true,
  suggestedFileName,
}) {
  if (!Array.isArray(classGroups) || classGroups.length === 0) {
    throw new Error("لا توجد شعب مختارة للتخريج");
  }
  if (!Array.isArray(selectedFields) || selectedFields.length === 0) {
    throw new Error("يجب اختيار عمود واحد على الأقل");
  }

  const wb = new ExcelJS.Workbook();
  wb.creator = "School Management System";
  wb.created = new Date();

  const headerLabels = selectedFields.map(
    (k) => STUDENT_FIELD_LABELS[k] || k
  );

  // --- group ClassGroup rows by className ---
  const byClassName = new Map();
  for (const g of classGroups) {
    const key = g.className || "بدون اسم";
    if (!byClassName.has(key)) byClassName.set(key, []);
    byClassName.get(key).push(g);
  }

  for (const className of sortClassNames([...byClassName.keys()])) {
    // sort by roomNumber so rooms appear in order on the sheet
    const groups = byClassName.get(className).slice().sort((a, b) =>
      String(a.roomNumber).localeCompare(String(b.roomNumber), "ar", {
        numeric: true,
      })
    );

    const ws = wb.addWorksheet(sanitizeSheetName(className), {
      views: [{ rightToLeft: true, state: "frozen", ySplit: 1 }],
      properties: { defaultRowHeight: 18 },
    });

    // ---------- header row ----------
    const headerRow = ws.addRow(headerLabels);
    headerRow.height = 28;
    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: { argb: "FFFFFFFF" },
        size: 12,
        name: "Arial",
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1E40AF" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
        readingOrder: "rtl",
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF1E3A8A" } },
        left: { style: "thin", color: { argb: "FF1E3A8A" } },
        bottom: { style: "thin", color: { argb: "FF1E3A8A" } },
        right: { style: "thin", color: { argb: "FF1E3A8A" } },
      };
    });

    // ---------- helpers (closed over the worksheet) ----------
    const writeRoomSeparator = (roomNumber) => {
      const sepRow = ws.addRow([`الشعبة رقم: ${roomNumber || "-"}`]);
      ws.mergeCells(sepRow.number, 1, sepRow.number, selectedFields.length);
      const sepCell = sepRow.getCell(1);
      sepCell.font = {
        bold: true,
        color: { argb: "FF1E3A8A" },
        size: 12,
        name: "Arial",
      };
      sepCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDBEAFE" },
      };
      sepCell.alignment = {
        horizontal: "center",
        vertical: "middle",
        readingOrder: "rtl",
      };
      sepCell.border = {
        top: { style: "thin", color: { argb: "FF93C5FD" } },
        bottom: { style: "thin", color: { argb: "FF93C5FD" } },
      };
      sepRow.height = 22;
    };

    let rowParity = 0;
    const writeStudentRow = (s) => {
      const values = selectedFields.map((k) => toExcelValue(k, s[k]));
      const dataRow = ws.addRow(values);
      dataRow.alignment = {
        horizontal: "center",
        vertical: "middle",
        readingOrder: "rtl",
        wrapText: true,
      };
      dataRow.font = { name: "Arial", size: 11 };
      dataRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFD1D5DB" } },
          left: { style: "thin", color: { argb: "FFD1D5DB" } },
          bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
          right: { style: "thin", color: { argb: "FFD1D5DB" } },
        };
      });
      if (rowParity % 2 === 0) {
        dataRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF8FAFC" },
          };
        });
      }
      selectedFields.forEach((k, idx) => {
        if (DATE_FIELDS.has(k)) {
          dataRow.getCell(idx + 1).numFmt = "dd/mm/yyyy";
        }
      });
      rowParity++;
    };

    // ---------- write rows: banded by room, or flat ----------
    if (groupByRoom) {
      for (const group of groups) {
        writeRoomSeparator(group.roomNumber);

        const enrollments = Array.isArray(group.students)
          ? group.students
          : [];
        if (enrollments.length === 0) {
          const emptyRow = ws.addRow([
            "لا يوجد طلاب مسجلون في هذه الشعبة",
          ]);
          ws.mergeCells(
            emptyRow.number,
            1,
            emptyRow.number,
            selectedFields.length
          );
          const c = emptyRow.getCell(1);
          c.font = {
            italic: true,
            color: { argb: "FF6B7280" },
            name: "Arial",
          };
          c.alignment = {
            horizontal: "center",
            vertical: "middle",
            readingOrder: "rtl",
          };
          continue;
        }

        for (const enrollment of enrollments) {
          writeStudentRow((enrollment && enrollment.student) || {});
        }
      }
    } else {
      // Flat mode: dump every student of every room for this الصف into one
      // continuous block, sorted by serialNumber so the order is stable
      // regardless of which room they came from.
      const allStudents = [];
      for (const group of groups) {
        const enrollments = Array.isArray(group.students)
          ? group.students
          : [];
        for (const enrollment of enrollments) {
          if (enrollment && enrollment.student) {
            allStudents.push(enrollment.student);
          }
        }
      }
      allStudents.sort((a, b) =>
        String(a.serialNumber || "").localeCompare(
          String(b.serialNumber || ""),
          "ar",
          { numeric: true }
        )
      );

      if (allStudents.length === 0) {
        const emptyRow = ws.addRow(["لا يوجد طلاب لهذا الصف"]);
        ws.mergeCells(
          emptyRow.number,
          1,
          emptyRow.number,
          selectedFields.length
        );
        const c = emptyRow.getCell(1);
        c.font = {
          italic: true,
          color: { argb: "FF6B7280" },
          name: "Arial",
        };
        c.alignment = {
          horizontal: "center",
          vertical: "middle",
          readingOrder: "rtl",
        };
      } else {
        for (const s of allStudents) writeStudentRow(s);
      }
    }

    // ---------- auto column widths (capped) ----------
    ws.columns.forEach((col, idx) => {
      let max = String(headerLabels[idx] || "").length;
      col.eachCell({ includeEmpty: false }, (cell) => {
        const v = cell.value;
        let len = 0;
        if (v == null) len = 0;
        else if (v instanceof Date) len = 12;
        else len = String(v).length;
        if (len > max) max = len;
      });
      col.width = Math.min(Math.max(max + 4, 12), 38);
    });

    // ---------- print setup ----------
    ws.pageSetup = {
      orientation: "landscape",
      paperSize: 9, // A4
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      horizontalCentered: true,
      margins: {
        left: 0.4,
        right: 0.4,
        top: 0.5,
        bottom: 0.5,
        header: 0.3,
        footer: 0.3,
      },
    };
  }

  // ---------- write & save ----------
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const baseName =
    (suggestedFileName || "الطلاب").toString().trim() || "الطلاب";
  const fileName = baseName.endsWith(".xlsx") ? baseName : `${baseName}.xlsx`;

  // File System Access API → real "Save As" dialog with file explorer.
  // Available in Chromium browsers (Chrome, Edge, Opera, etc.).
  if (
    typeof window !== "undefined" &&
    typeof window.showSaveFilePicker === "function"
  ) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [
          {
            description: "Excel Workbook",
            accept: {
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                [".xlsx"],
            },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { saved: true, method: "picker", fileName };
    } catch (err) {
      // User dismissed the picker → don't fall back to a silent download.
      if (err && err.name === "AbortError") {
        return { saved: false, cancelled: true };
      }
      // Anything else (insecure context, permission denied, etc.) → fall back
      // to a regular download so the user still ends up with the file.
      console.warn(
        "showSaveFilePicker failed, falling back to download:",
        err
      );
    }
  }

  // Fallback: triggers the browser download dialog (which on Windows respects
  // the user's "ask where to save" setting and pops a save-as window).
  saveAs(blob, fileName);
  return { saved: true, method: "download", fileName };
}
